<script setup>
import {defineProps, computed, nextTick, ref} from "vue";
import ContentHubItem from "@/components/ContentHub/ContentHubItem.vue";
import FilterDesktop from "@/components/ContentHub/ContentHubFilterDesktop";
import FilterMobile from "@/components/ContentHub/ContentHubFilterMobile";
import {breakpointState} from "@/utils";
import useSession from "@/utils/session";
import {useI18n} from "vue-i18n";

const Isotope = require("isotope-layout");

const props = defineProps({
    config: {
        type: Object,
        required: true,
    },
});

const session = ref(useSession("contenthub", {
    activeCategory: 0,
    activeSubCategories: [],
    pager: {
        page: 1,
        per: 6,
    }
}));

/**
 * Breakpoint Config
 */
const isMobile = computed(() => {
    return breakpointState.value.down.indexOf("tablet_portrait") === -1;
});

/**
 * Isotope References
 */
const isotopeAnimation = ref();
const isotopeContainer = ref();
const iso = ref();
const items = ref([]);
const hiddenItemCount = ref(0);

/**
 * Pager
 */
let itemCount = 0;

const pagerCount = computed(() => {
    return session.value.pager.page * session.value.pager.per;
});

function nextPage() {
    session.value.pager.page++;
    filterItems();
}

/**
 * Translation
 */
const {t} = useI18n({
    messages: {
        en: {
            ContentHubLoadMore: "load more",
        },
        es: {
            ContentHubLoadMore: "Mostrar más",
        },
        de: {
            ContentHubLoadMore: "Mehr",
        },
        pt: {
            ContentHubLoadMore: "Carregar mais",
        },
        ru: {
            ContentHubLoadMore: "Загрузить больше",
        },
        tr: {
            ContentHubLoadMore: "Daha fazla yükle",
        },
        zh: {
            ContentHubLoadMore: "加载更多",
        },
    },
});

/**
 * Category Config
 */
const categories = ref(props.config.firstLevelFilters);
const subCategories = ref([]);

function openCategory(category) {
    if (session.value.activeCategory !== category.value) {
        isotopeAnimation.value.classList.add("contenthub__items--loading");
        if (items.value.length) {
            isotopeAnimation.value.classList.add("contenthub__items--fadeout");
            isotopeAnimation.value.classList.remove(
                "contenthub__items--fadein"
            );
        }

        session.value.activeCategory = category.value;
        session.value.activeSubCategories = [];

        if (iso.value) {
            iso.value.destroy();
        }

        session.value.pager.page = 1;

        fetchCategory(category.link);
    }
}

function fetchCategory(link) {
    fetch(link, {
        method: "GET",
    })
        .then((response) => response.json())
        .then((response) => {
            setTimeout(() => {
                items.value = [];

                subCategories.value = response.secondLevelFilters ?? [];
                items.value = response.items ?? [];
                isotopeAnimation.value.classList.add(
                    "contenthub__items--fadein"
                );
                isotopeAnimation.value.classList.remove(
                    "contenthub__items--loading"
                );
                isotopeAnimation.value.classList.remove(
                    "contenthub__items--fadeout"
                );

                nextTick(() => {
                    itemCount = 0;
                    hiddenItemCount.value = 0;

                    iso.value = new Isotope(isotopeContainer.value, {
                        layoutMode: "fitRows",
                        itemSelector: ".contenthub__item",
                        getSortData: {
                            weight: function (itemElem) {
                                return itemElem.classList.contains('contactcard') ? 1 : 0;
                            }
                        }
                    });
                    filterItems();
                });
            }, 300);
        });
}

function setSubCategories(subCategories) {
    session.value.activeSubCategories = subCategories;
    filterItems();
}

function filterItems() {
    itemCount = 0;
    hiddenItemCount.value = 0;
    iso.value.arrange({
        filter: filterFunction,
        sortBy: 'weight'
    });
}

function filterFunction(item) {
    const itemCategories = item.dataset.categories
        ? JSON.parse(item.dataset.categories)
        : [0];

    if (session.value.activeSubCategories.length === 0) {
        if (itemCount >= pagerCount.value) {
            hiddenItemCount.value++;
            return false;
        }
        itemCount++;
        return true;
    } else {
        for (let x = 0; x < itemCategories.length; x++) {
            if (
                itemCategories.indexOf(0) !== -1 ||
                session.value.activeSubCategories.indexOf(itemCategories[x]) !== -1

            ) {
                if (itemCount >= pagerCount.value) {
                    hiddenItemCount.value++;
                    return false;
                }
                itemCount++;
                return true;
            }
        }
    }
    return false;
}

if (session.value.activeCategory !== 0) {
    let c = categories.value.find(i => {
        return i.value === session.value.activeCategory;
    });
    fetchCategory(c.link);
}
</script>

<template>
    <div class="contenthub">
        <FilterDesktop
            v-if="!isMobile"
            :categories="categories"
            :sub-categories="subCategories"
            :active-category="session.activeCategory"
            :active-sub-categories="session.activeSubCategories"
            @setCategory="openCategory"
            @setSubCategories="setSubCategories"
        />
        <FilterMobile
            v-if="isMobile"
            :categories="categories"
            :sub-categories="subCategories"
            :active-category="session.activeCategory"
            :active-sub-categories="session.activeSubCategories"
            @setCategory="openCategory"
            @setSubCategories="setSubCategories"
        />
        <div
            ref="isotopeAnimation"
            class="contenthub__items"
            v-show="items.length"
        >
            <div ref="isotopeContainer" class="contenthub__items--wrap">
                <ContentHubItem
                    v-for="item in items"
                    :key="item.id"
                    :item="item"
                />
            </div>
        </div>
        <div
            class="contenthub__pager"
            v-if="items.length"
        >
            <button
                type="button"
                class="mdc-button--primary"
                @click="nextPage"
                v-if="hiddenItemCount > 0"
            >
                <span
                    class="mdc-button__label"
                    v-text="t('ContentHubLoadMore')"
                />
            </button>
        </div>
    </div>
</template>

<style lang="scss">
@use "@/scss/design-library/functions/calculate";
@use "@/scss/design-library/variables";
@use "@/scss/design-library/colors.scss";
@use "@/scss/design-library/grid";
@use "sass:math";

$fadeDuration: 300ms;

.frame-row-contenthub_teaser {
    background-color: #f5f5f5;
}

.frame.frame-type-contenthub_teaser {
    header {
        margin: 32px 0;

        .ce-headline-center,
        .ce-headline {
            padding-top: 0;
            margin-top: 0;
        }
    }
}

.contenthub {
    margin-top: 32px;

    &__items {
        padding-top: 32px;
        padding-bottom: 42px;

        &--loading {
            opacity: 0;
        }

        &--fadein {
            animation-name: contentHubFadeIn;
            animation-duration: #{$fadeDuration};
        }

        @keyframes contentHubFadeIn {
            from {
                opacity: 0;
            }
            to {
                opacity: 1;
            }
        }

        &--fadeout {
            animation-name: contentHubFadeOut;
            animation-duration: #{$fadeDuration};
        }

        @keyframes contentHubFadeOut {
            from {
                opacity: 1;
            }
            to {
                opacity: 0;
            }
        }

        &--wrap {
            overflow: hidden;
            @each $size, $gutter in grid.$default-gutter {
                @include grid.media-query_($size) {
                    margin: -#{math.div($gutter, 2)};
                }
            }
            @include grid.media-query_(mobile) {
                margin: 0;
            }

            &:after {
                content: "";
                clear: both;
            }
        }
    }

    &__separator {
        margin: 0;
        padding: 0;
        height: 0;
        border: 1px solid #{colors.$grey};
    }

    &__pager {
        height: 20px;
        text-align: center;

        border-bottom: 2px solid #{colors.$grey};

        button {
            margin-bottom: -20px;
        }
    }
}
</style>
